import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Paper,
  InputAdornment,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Snackbar,
  Alert,
} from "@mui/material";
import { Add, Search, Edit, Delete } from "@mui/icons-material";
import { useLinks } from "../hooks/useLinks";
import { apiService } from "../api/services/apiService";
import { PaginationControls } from "../components/common/PaginationControls";
import { useNavigate } from "react-router-dom";
import { Link, EntityStatus } from "../types";
import { linkToEditDraft } from "../utils/linkFields";

const ITEMS_PER_PAGE = 10;

const ManageLinks: React.FC = () => {
  const navigate = useNavigate();
  const { links: allLinks, loading, error } = useLinks();

  const [links, setLinks] = useState<Link[]>([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // delete
  const [deleteUuid, setDeleteUuid] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // edit
  const [editingLink, setEditingLink] = useState<Link | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [updating, setUpdating] = useState(false);

  const [successOpen, setSuccessOpen] = useState(false);

  useEffect(() => {
    setLinks(allLinks);
  }, [allLinks]);

  /* ---------------- Edit ---------------- */

  const handleEditClick = (link: Link) => {
    setEditingLink(linkToEditDraft(link));
    setEditOpen(true);
  };

  const handleUpdateLink = async () => {
    if (!editingLink) return;

    setUpdating(true);
    try {
      const updated = await apiService.updateLink(editingLink.uuid, {
        name: editingLink.name,
        entityClass: editingLink.entityClass?.trim() || undefined,
        description: editingLink.description?.trim() || undefined,
        status: editingLink.status,
      });

      setLinks((prev) =>
        prev.map((l) => (l.uuid === updated.uuid ? updated : l)),
      );

      setEditOpen(false);
      setEditingLink(null);
      setSuccessOpen(true);
    } catch (err: any) {
      alert(err.message || "Failed to update link");
    } finally {
      setUpdating(false);
    }
  };

  /* ---------------- Delete ---------------- */

  const handleDeleteClick = (uuid: string) => {
    setDeleteUuid(uuid);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteUuid) return;

    setIsDeleting(true);
    try {
      const success = await apiService.deleteLink(deleteUuid);
      if (success) {
        setLinks((prev) => prev.filter((l) => l.uuid !== deleteUuid));
      }
    } catch (err: any) {
      alert(err.message || "Failed to delete link");
    } finally {
      setIsDeleting(false);
      setDeleteOpen(false);
      setDeleteUuid(null);
    }
  };

  /* ---------------- Filtering & Pagination ---------------- */

  const filteredLinks = links.filter((l) =>
    l.name.toLowerCase().includes(search.toLowerCase()),
  );

  const totalPages = Math.ceil(filteredLinks.length / ITEMS_PER_PAGE);
  const paginatedLinks = filteredLinks.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Links
        </Typography>

        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate("/manage-assets/links/add")}
          >
            Add Link
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && !error && (
        <>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedLinks.map((link) => (
                  <TableRow key={link.uuid} hover>
                    <TableCell>{link.name}</TableCell>
                    <TableCell>
                      <Chip
                        label={link.status}
                        size="small"
                        color={link.status === "ACTIVE" ? "success" : "default"}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleEditClick(link)}>
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteClick(link.uuid)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* Edit Dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth>
        <DialogTitle>Edit Link</DialogTitle>
        <DialogContent>
          {editingLink && (
            <>
              <TextField
                fullWidth
                label="Name"
                margin="normal"
                value={editingLink.name}
                onChange={(e) =>
                  setEditingLink({ ...editingLink, name: e.target.value })
                }
              />

              <TextField
                fullWidth
                label="URL"
                margin="normal"
                value={editingLink.entityClass ?? ""}
                onChange={(e) =>
                  setEditingLink({
                    ...editingLink,
                    entityClass: e.target.value,
                  })
                }
              />

              <TextField
                fullWidth
                label="Description"
                margin="normal"
                multiline
                minRows={2}
                value={editingLink.description ?? ""}
                onChange={(e) =>
                  setEditingLink({
                    ...editingLink,
                    description: e.target.value,
                  })
                }
              />

              <TextField
                fullWidth
                label="Tenant ID"
                margin="normal"
                value={editingLink.tenantId}
                disabled
              />

              <TextField
                select
                fullWidth
                label="Status"
                margin="normal"
                value={editingLink.status}
                onChange={(e) =>
                  setEditingLink({
                    ...editingLink,
                    status: e.target.value as EntityStatus,
                  })
                }
              >
                <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                <MenuItem value="INACTIVE">INACTIVE</MenuItem>
              </TextField>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUpdateLink}
            disabled={updating}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <strong>Delete Link?</strong>
        </DialogTitle>
        <DialogContent>
          Are you sure you want to delete this link?
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={isDeleting}
            variant="outlined"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            No
          </Button>
          <Button
            color="error"
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600 }}
            onClick={handleConfirmDelete}
            disabled={isDeleting}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={successOpen}
        autoHideDuration={2000}
        onClose={() => setSuccessOpen(false)}
      >
        <Alert severity="success">Link updated successfully</Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageLinks;
